import { MapContainer, FeatureGroup, useMapEvents, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { useRef, useEffect, useCallback, useState } from 'react';
import L from 'leaflet'
import { FormModal } from '../layout/Modal/Modal';
import ParcelForm from '../form/ParcelForm';
import { ParcelData } from "../../interfaces/ParcelData";
import TaskOverviewForm from '../form/TaskOverviewForm/TaskOverviewForm';
import ParcelService from '../../services/parcelService';
import { useSharedState } from '../../config/FarmContext';
import { DeleteModal } from '../layout/deleteModal';
import LayersControlComponent from './LayerControlComponent';
import GeometryUtil from "leaflet-geometryutil";
import { Snackbar } from '../common/Snackbar';
import { SnackbarProps } from '../common/SnackbarProps';
import { CgDanger } from "react-icons/cg";
import './Map.css';
import { ParcelCycle } from '../../interfaces/ParcelCycle';

const DEFAULT_LAYER_COLOR = '#1b4a07';
const GEOMETRY_TYPES = ['Polygon', 'Rectangle'];

// @ts-ignore
L.drawLocal.edit.toolbar.actions.save.text = "Speichern";
// @ts-ignore
L.drawLocal.edit.toolbar.actions.cancel.text = "Abbrechen";
// @ts-ignore
L.drawLocal.draw.toolbar.actions.text = "Abbrechen";
// @ts-ignore
L.drawLocal.draw.toolbar.finish.text = "Speichern";
// @ts-ignore
L.drawLocal.draw.toolbar.undo.text = "Letzten Punkt löschen";
// @ts-ignore
L.drawLocal.edit.toolbar.actions.clearAll.text = "Alles löschen";

export const MapComponent = () => {
    const featureGroupRef = useRef<L.FeatureGroup | null>(null);
    const parcelService = new ParcelService();
    const [editable, setEditable] = useState(false);
    const [showCreateModal, setCreateModal] = useState(false);
    const [showModal, setModal] = useState(false);
    const [currentParcel, setcurrentParcel] = useState<ParcelData | null>(null);
    const [mapKey, setMapKey] = useState(0);
    const [zoom, setZoom] = useState(15);
    const [position, setPosition] = useState<[number, number]>([47.582, 9.200]);
    const { farm } = useSharedState();
    const [showDeleteModal, setDeleteModal] = useState(false);
    const [deleteConfirmed, setDeleteConfirmed] = useState<((confirmed: boolean) => void) | null>(null);
    const [snackbar, setSnackbar] = useState<SnackbarProps>({ active: false })
    const [deleteLayerNames, setDeleteLayerNames] = useState<string[]>([])
    const [loading, setLoading] = useState(false);
    const [currentCycle, setCurrentCycle] = useState<ParcelCycle | undefined>(undefined)

    const toggleCreateModal = () => {
        setCreateModal((prev) => !prev);
        if (showCreateModal) {
            setMapKey((prev) => prev + 1);
        }
    };

    const handleOnSaveParcel = async (response: SnackbarProps) => {
        toggleCreateModal()
        triggerSnackbar(response.message!, response.type!);
        reload()
    }

    const triggerSnackbar = (message: string, type: "success" | "error" | "info" | "warning") => {
        setSnackbar({ message: message, type: type, active: true });
        setTimeout(() => {
            setSnackbar({ active: false });
        }, 3000);
    };

    const toggleModal = () => {
        setModal((prev) => !prev);
    };

    const reload = () => {
        loadLayers();
        setMapKey((prev) => prev + 1);
    }

    const setLayerStyle = useCallback((layer: L.Layer, color: string = DEFAULT_LAYER_COLOR) => {
        (layer as L.Polyline).setStyle({
            color,
            weight: 3,
            opacity: 0.7,
            fillColor: color,
            fillOpacity: 0.56,
        });
    }, []);

    const loadLayers = async () => {
        try {
            setLoading(true)
            const featureGroup = featureGroupRef.current;
            if (!featureGroup) {
                return;
            }

            if (!farm || !farm?.id)
                return;

            const parcels = await parcelService.getParcelByFarmId(Number(farm?.id));

            if (parcels && parcels.length > 0) {
                parcels.forEach((parcel: ParcelData) => {
                    let geoJSON = JSON.parse(parcel.geometry!);

                    try {
                        geoJSON.properties.id = parcel.id;
                        geoJSON.properties.name = parcel.name;
                    } catch {
                        return;
                    }

                    const layer = new L.GeoJSON(geoJSON).getLayers()[0];
                    if (GEOMETRY_TYPES.includes((layer as any).feature.geometry?.type)) {
                        setLayerStyle(layer, geoJSON.properties?.color);
                        featureGroup!.addLayer(layer);
                    }
                });
            }
        } catch (e) {
            triggerSnackbar("Parzellen konnten nicht geladen werden ❌", "error")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const initializeFeatureGroup = () => {
            loadLayers();
        };
        const timeout = setTimeout(initializeFeatureGroup, 0);
        return () => clearTimeout(timeout);

    }, [mapKey]);

    useEffect(() => {
        if (farm) {
            setPosition([farm.coordinatesX!, farm.coordinatesY!])
            setZoom(15)
            setMapKey((prev) => prev + 1);
        }

    }, [farm?.coordinatesX]);

    const handleCreated = (e: any) => {
        const featureGroup = featureGroupRef.current;
        if (!featureGroup) return;

        const layer = e.layer;
        setLayerStyle(layer, DEFAULT_LAYER_COLOR);

        const geoJSON = layer.toGeoJSON();
        geoJSON.properties = geoJSON.properties || {};
        geoJSON.properties.color = DEFAULT_LAYER_COLOR;

        layer.feature = { ...layer.feature, properties: geoJSON.properties };
        featureGroup.addLayer(layer);
        setcurrentParcel({
            geometry: JSON.stringify(geoJSON),
        });
        toggleCreateModal()
    };

    const handleEditStart = () => setEditable(true);
    const handleEditStop = () => setEditable(false);

    const handleEdited = (e: any) => {
        e.layers.eachLayer(async (layer: L.Layer) => {
            const geoJSON = (layer as any).toGeoJSON();
            try {
                await parcelService.editById(geoJSON.properties.id, { geometry: JSON.stringify({ type: "Feature", ...geoJSON }) });
            } catch (error: any) {
                triggerSnackbar("Fehler beim bearbeiten der Parzelle ❌", "error");
                setMapKey((prev) => prev + 1);
                return;
            }
        })
        triggerSnackbar("Parzellen erfolgreich bearbeitet ✅", "success")
    }

    const showDeleteConfirmation = (): Promise<boolean> => {
        return new Promise((resolve) => {
            setDeleteModal(true);

            const handleUserChoice = (confirmed: boolean) => {
                setDeleteModal(false);
                resolve(confirmed);
            };

            setDeleteConfirmed(() => handleUserChoice);
        });
    };

    const getLayerNames = (layers: L.LayerGroup<any>): string[] => {
        return layers.getLayers().map(layer => {
            const geoJSON = (layer as any).toGeoJSON();
            return geoJSON.properties.name || "Unbenannte Parzelle";
        });
    };


    const handleDeleted = async (e: any) => {

        const layerNames = getLayerNames(e.layers);
        setDeleteLayerNames(layerNames);

        const isConfirmed = await showDeleteConfirmation();

        if (!isConfirmed) {
            setMapKey((prev) => prev + 1);
            return;
        }
        const featureGroup = featureGroupRef.current;
        const length = e.layers.getLayers().length;

        e.layers.eachLayer(async (layer: L.Layer) => {
            const geoJSON = (layer as any).toGeoJSON();
            try {
                await parcelService.deleteById(geoJSON.properties.id);
                featureGroup!.removeLayer(layer)
            } catch (error: any) {
                featureGroup!.addLayer(layer);
                triggerSnackbar("Fehler beim Löschen der Parzelle ❌", "error");
                return;
            }
        });
        triggerSnackbar(`${length} Parzellen erfolgreich gelöscht ✅`, "success")
        setDeleteLayerNames([])
    };

    const EventHandler = () => {
        const map = useMap();

        useMapEvents({
            click: async (event) => {
                if (editable) return;

                const featureGroup = featureGroupRef.current;
                if (!featureGroup) return;

                setLoading(true);

                const { latlng } = event;
                const layers = featureGroup.getLayers().filter(layer =>
                    layer instanceof L.Polygon || layer instanceof L.Rectangle
                ) as (L.Polygon | L.Rectangle)[];

                const closest = GeometryUtil.closestLayer(map, layers, latlng);

                if (closest?.layer) {

                    if (!closest.layer.getBounds().contains(latlng)) {
                        setLoading(false);
                        return;
                    }
                    const geoJSON = (closest.layer as any).toGeoJSON();
                    try {
                        const cycle =  parcelService.getCurrentCycleForParcel(geoJSON.properties.id)
                        const parcel =  parcelService.getParcelById(geoJSON.properties.id)
                        setCurrentCycle((await cycle))
                        setcurrentParcel(await parcel);
                        toggleModal()
                    } catch (error: any) {
                        triggerSnackbar("Parzelle konnte nicht geladen werden ❌", "error");
                    } finally {
                        setLoading(false);
                    }
                }
            }
        })
        return null;
    }

    const MapHandler = () => {
        const map = useMap();

        useEffect(() => {
            const updateZoom = () => setZoom(map.getZoom());
            const updatePosition = () => setPosition([map.getCenter().lat, map.getCenter().lng]);

            map.on('zoomend', updateZoom);
            map.on('moveend', updatePosition);

            return () => {
                map.off('zoomend', updateZoom);
                map.off('moveend', updatePosition);
            };
        }, [map]);

        return null;
    };

    return (
        <div>
            {loading && <div className="progress-container !h-2">
                <div className="progress-bar"></div>
            </div>}

            <MapContainer key={mapKey} center={position} zoom={zoom} style={{ height: "100dvh" }}>
                <LayersControlComponent />

                <FeatureGroup ref={featureGroupRef}>
                    <EditControl
                        position="topright"

                        edit={{
                            draggable: true,
                        }}

                        draw={{
                            rectangle: true,
                            polygon: true,
                            circle: false,
                            marker: false,
                            polyline: false,
                            circlemarker: false
                        }}
                        onCreated={handleCreated}
                        onEdited={handleEdited}
                        onDeleted={handleDeleted}
                        onEditStart={handleEditStart}
                        onEditStop={handleEditStop}
                        onDrawStart={handleEditStart}
                        onDrawStop={handleEditStop}
                        onDeleteStart={handleEditStart}
                        onDeleteStop={handleEditStop}
                    />
                </FeatureGroup>
                <EventHandler />
                <MapHandler />
            </MapContainer>
            <FormModal open={showCreateModal} onClose={toggleCreateModal}>
                <ParcelForm onSave={handleOnSaveParcel} parcel={currentParcel!} />
            </FormModal>
            {currentCycle &&
                <FormModal open={showModal} onClose={toggleModal}>
                    <TaskOverviewForm parcel={currentParcel!} cycle={currentCycle!} onEditParcel={reload} />
                </FormModal>
            }

            <FormModal open={showDeleteModal}>
                <DeleteModal onSubmit={deleteConfirmed!}>
                    <p style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <b>Parzellen</b> wirklich endgültig löschen? <CgDanger size={22} />
                    </p>
                    {deleteLayerNames.map((name) => (<p><b>{name}</b></p>))}
                </DeleteModal>
            </FormModal>
            {snackbar.active && <Snackbar message={snackbar.message!} type={snackbar.type} />}
        </div>
    );
}